import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from './ui/Button';
import { Target, FileText, Globe, Send, BarChart3, Sparkles } from 'lucide-react';
interface SlideOperatingLayerProps {
  onContinue: () => void;
}
// Slide 4 — What Snakker Takes Over (operating layer)
export function SlideAIOpportunity({
  onContinue
}: SlideOperatingLayerProps) {
  const [activeCard, setActiveCard] = useState<number | null>(null);
  const [stage, setStage] = useState(-1);
  const cards = [{
    icon: <Target className="w-5 h-5" data-id="element-14" />,
    title: 'Campaign strategy',
    desc: 'Per-brand and per-country plan: what to say, to whom, on which channel.'
  }, {
    icon: <FileText className="w-5 h-5" data-id="element-15" />,
    title: 'Interactive Snak content',
    desc: 'Quiz-based, mobile-first assets built around your approved materials.'
  }, {
    icon: <Globe className="w-5 h-5" data-id="element-16" />,
    title: 'Country adaptation',
    desc: 'Localization framework that scales across West African markets.'
  }, {
    icon: <Send className="w-5 h-5" data-id="element-17" />,
    title: 'Channel-ready assets',
    desc: 'WhatsApp, QR, email, web — compliant and conversion-optimized.'
  }, {
    icon: <BarChart3 className="w-5 h-5" data-id="element-18" />,
    title: 'Real-time reporting',
    desc: 'Country and brand-level engagement dashboards. No spreadsheets.'
  }, {
    icon: <Sparkles className="w-5 h-5" data-id="element-19" />,
    title: 'Optimization',
    desc: 'Automated alerts and recommendations on what to push next.'
  }];
  useEffect(() => {
    const timers = cards.map((_, idx) => setTimeout(() => setStage(idx), 300 + idx * 200));
    const final = setTimeout(() => setStage(cards.length), 300 + cards.length * 200 + 200);
    return () => {
      timers.forEach(clearTimeout);
      clearTimeout(final);
    };
  }, []);
  return <div className="h-full w-full flex flex-col px-8 py-10 md:px-20 md:py-14 max-w-7xl mx-auto" data-id="element-20">
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} data-id="element-21">
        <span className="text-teal-400 text-xs uppercase tracking-widest font-medium" data-id="element-22">
          The operating layer
        </span>
        <h2 className="text-3xl md:text-5xl font-serif text-white mt-3 mb-4" data-id="element-23">
          Snakker becomes the{' '}
          <span className="text-teal-400" data-id="element-24">campaign operating layer.</span>
        </h2>
        <p className="text-lg text-white/60 max-w-2xl" data-id="element-25">
          A managed engine that runs the full cycle — so you keep control of the
          brand, and we carry the execution.
        </p>
      </motion.div>

      <div className="flex-1 relative flex items-center justify-center mt-8" data-id="element-26">
        {/* Loop cards in grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 w-full max-w-4xl" data-id="element-27">
          {cards.map((card, idx) => <motion.button key={idx} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: stage >= idx ? 1 : 0,
          y: stage >= idx ? 0 : 20
        }} onClick={() => setActiveCard(activeCard === idx ? null : idx)} className={`text-left bg-navy-700/40 border rounded-2xl p-4 transition-all ${activeCard === idx ? 'border-teal-400 bg-teal-400/5' : 'border-navy-600 hover:border-teal-400/40'}`} data-id="element-28">
              <div className="flex items-center gap-2.5 mb-2" data-id="element-29">
                <div className="w-9 h-9 rounded-lg bg-teal-400/10 border border-teal-400/20 flex items-center justify-center text-teal-400" data-id="element-30">
                  {card.icon}
                </div>
                <h3 className="text-white font-semibold text-sm" data-id="element-31">
                  {card.title}
                </h3>
              </div>
              <motion.p initial={false} animate={{
            height: activeCard === idx ? 'auto' : 0,
            opacity: activeCard === idx ? 1 : 0
          }} className="text-white/60 text-xs leading-relaxed overflow-hidden" data-id="element-32">
                <span className="block pt-1" data-id="element-33">{card.desc}</span>
              </motion.p>
              {activeCard !== idx && <p className="text-white/40 text-[10px] mt-1" data-id="element-34">
                  Click to expand
                </p>}
            </motion.button>)}
        </div>
      </div>

      <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: stage >= cards.length ? 1 : 0
    }} className="mt-6 flex items-center justify-between gap-4 bg-teal-400/5 border border-teal-400/20 rounded-xl px-6 py-4" data-id="element-35">
        <p className="text-teal-300 font-medium" data-id="element-36">
          You keep control of the brand. Snakker carries the execution.
        </p>
        <Button onClick={onContinue} data-id="element-37">Follow the model →</Button>
      </motion.div>
    </div>;
}